import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  LoaderCircle,
  Search,
  ChevronDown,
  ChevronRight,
  EyeOff,
} from 'lucide-react';
import { api } from '@/lib/api';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useProject } from '@/lib/ProjectContext';
import { Telemetry } from '@/lib/telemetry';
import { useScanTask } from '@/lib/useScanTask';
import { useDashboardCache } from '@/lib/DashboardCacheContext';
import { usePermissions } from '@/lib/usePermissions';
import { useSubmissionFilter } from '@/lib/useSubmissionFilter';
import { useScanStatus } from '@/lib/ScanStatusContext';
import { invalidateConsistency, onConsistencyInvalidated } from '@/lib/consistencyInvalidation';

import { ScanProgress } from '@/components/shared/ScanProgress';
import { ScanFooter } from '@/components/shared/ScanFooter';
import type { DoseUnitScanResponse, DoseUnitEntry, DosageVariation, ScanIntegrityWarning } from '@/types/api';
import { DataIntegrityNotice } from '@/components/shared/DataIntegrityNotice';
import { TierUncertaintyIndicator } from '@/components/shared/TierUncertaintyIndicator';
import type { FindingTier } from '@/lib/findingClassification';
import { countByScope } from '@/lib/findingClassification';
import { ScopePill } from '@/components/shared/ScopePill';
import { ScopeFilterSelect, applyScopeFilter, type ScopeFilter } from '@/components/shared/ScopeFilterSelect';
import { ScopeBreakdownCard } from '@/components/shared/ScopeBreakdownCard';
import { SummaryStatCard } from '@/components/shared/SummaryStatCard';
import { formatSectionLabel } from '@/lib/formatSectionLabel';

type UnitCategory = 'Weight' | 'Weight-normalized' | 'Rate' | 'Concentration' | 'Other';

const UNIT_CATEGORY_ORDER: UnitCategory[] = ['Weight', 'Weight-normalized', 'Rate', 'Concentration', 'Other'];

function classifyUnit(dosage: string): UnitCategory {
  const unit = dosage.replace(/^[\d.,\s]+/, '').trim().toLowerCase();
  if (/\/kg\b/.test(unit)) return 'Weight-normalized';
  if (/\/(day|hr|hour|min|week)\b/.test(unit)) return 'Rate';
  if (/\/(ml|l|dl)\b/i.test(unit)) return 'Concentration';
  if (/^(mg|g|grams?|mcg|µg|kg|ug)\b/.test(unit)) return 'Weight';
  return 'Other';
}

function groupByUnit(variations: DosageVariation[]): { category: UnitCategory; items: DosageVariation[] }[] {
  const groups = new Map<UnitCategory, DosageVariation[]>();
  for (const v of variations) {
    const cat = classifyUnit(v.dosage);
    const list = groups.get(cat) ?? [];
    list.push(v);
    groups.set(cat, list);
  }
  return UNIT_CATEGORY_ORDER
    .filter(cat => groups.has(cat))
    .map(cat => ({ category: cat, items: groups.get(cat)! }));
}

type UIState = 'loading' | 'landing' | 'scanning' | 'results';
type StatusFilter = 'all' | 'consistent' | 'inconsistent';
type TieredEntry = DoseUnitEntry & { tier: FindingTier };

const PAGE_SIZE = 10;

export function DoseUnitPage() {
  const { activeProject } = useProject();
  const { invalidate: invalidateDashboard } = useDashboardCache();
  const { canRunScans } = usePermissions();
  const { submissionId, submissions, isFiltering, classifyTier } = useSubmissionFilter();
  const [overlayApplicable, setOverlayApplicable] = useState(false);
  const [uiState, setUiState] = useState<UIState>('loading');
  const [data, setData] = useState<DoseUnitScanResponse | null>(null);
  const [integrityWarnings, setIntegrityWarnings] = useState<ScanIntegrityWarning[]>([]);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [hideDismissed, setHideDismissed] = useState(true);
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>(isFiltering ? 'this-sub' : 'all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [toast, setToast] = useState('');
  const scanStatus = useScanStatus();
  const scanBusy = scanStatus.isScanning('dose-unit');
  const projectId = activeProject?.id;
  const onScanComplete = useCallback(() => {
    invalidateDashboard();
    if (projectId) invalidateConsistency(projectId);
  }, [invalidateDashboard, projectId]);
  const { taskState, startTask, cancel: cancelScan, reset: resetTask, result: scanResult } = useScanTask<DoseUnitScanResponse>('dose_unit', onScanComplete);

  const dismissedCount = useMemo(
    () => data?.substances.filter((s) => s.dismissed).length ?? 0,
    [data],
  );

  // A2F-462 follow-up: read via aggregate endpoint.
  const loadCached = useCallback(() => {
    if (!projectId) return;
    setUiState('loading');
    setError('');
    api.consistency.get(projectId, submissionId ?? undefined)
      .then((result) => {
        setOverlayApplicable(result.overlayApplicable);
        // A2F-832: capture warnings for dose_unit_inconsistent.
        setIntegrityWarnings(
          (result.dataIntegrityWarnings ?? []).filter(
            (w) => w.featureId === 'dose_unit_inconsistent',
          ),
        );
        const val = result.perFeature?.doseUnitInconsistent?.value as DoseUnitScanResponse | null | undefined;
        if (val) {
          setData(val);
          setUiState('results');
        } else {
          api.tasks.findRunning(projectId, 'dose_unit_scan')
            .then((task) => {
              setUiState('scanning');
              startTask(task.taskId);
            })
            .catch(() => {
              setUiState('landing');
            });
        }
      })
      .catch(() => {
        setUiState('landing');
      });
  }, [projectId, submissionId, startTask]);

  useEffect(() => {
    loadCached();
  }, [loadCached]);

  useEffect(() => {
    return onConsistencyInvalidated((pid) => {
      if (pid === projectId) loadCached();
    });
  }, [projectId, loadCached]);

  // Handle scan result from background task
  useEffect(() => {
    if (!scanResult) return;
    scanStatus.endScan('dose-unit');
    if (scanResult.stoppedEarly && scanResult.errors?.length) {
      const msg = scanResult.errors[0].message || 'Scan failed';
      const isAuthError = /api[_\s]?key.*not set|authentication|unauthorized/i.test(msg);
      setError(
        isAuthError
          ? 'No LLM API key configured. Go to Settings \u2192 Manage App to add your API key.'
          : msg,
      );
      setUiState(data ? 'results' : 'landing');
      return;
    }
    setData(scanResult);
    setUiState('results');
    Telemetry.reviewResultsViewed({
      feature: 'dose_unit',
      finding_count: scanResult.substances?.length ?? 0,
      stale_files: scanResult.staleFiles ?? 0,
    });
  }, [scanResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const startScan = async () => {
    if (!projectId) return;
    if (!scanStatus.startScan('dose-unit')) return;
    setUiState('scanning');
    setError('');
    resetTask();
    try {
      const overlayId = overlayApplicable ? (submissionId ?? undefined) : undefined;
      const { taskId } = await api.doseUnit.scan(projectId, overlayId);
      startTask(taskId);
    } catch (err: unknown) {
      scanStatus.endScan('dose-unit');
      const msg = err instanceof Error ? err.message : 'Failed to start scan';
      setError(msg);
      setUiState(data ? 'results' : 'landing');
      Telemetry.errorSwallowed({ error_type: 'scan_start_failure', operation: 'dose_unit_scan', screen_name: '/review/dose-unit', error_message: msg });
    }
  };

  const rescan = () => {
    if (!projectId) return;
    Telemetry.reviewCacheCleared({ feature: 'dose_unit' });
    api.doseUnit.clearCache(projectId).then(() => startScan());
  };

  const toggleRow = (name: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleOpenDoc = useCallback(
    async (docName: string, substanceName: string) => {
      if (!data) return;
      const docId = data.documentMap[docName];
      try {
        await navigator.clipboard.writeText(substanceName);
      } catch {
        // Clipboard may fail in non-secure contexts
      }
      if (docId) {
        api.documents.openLocal(docId).catch(() => {});
      }
      showToast(`Copied "${substanceName}" to clipboard — use Ctrl+F to find`);
    },
    [data],
  );

  const handleDismiss = useCallback(
    async (canonicalName: string, reason: string, dosageKey?: string) => {
      if (!projectId || !data) return;
      await api.doseUnit.dismiss(projectId, canonicalName, reason, dosageKey);
      Telemetry.findingDismissed({ feature: 'dose_unit', finding_id: dosageKey ? `${canonicalName}:${dosageKey}` : canonicalName, has_rationale: reason.trim().length >= 10 });
      setData({
        ...data,
        substances: data.substances.map((s) => {
          if (s.canonicalName !== canonicalName) return s;
          if (dosageKey === undefined) {
            // Whole-substance dismissal
            return {
              ...s,
              dismissed: true,
              dismissReason: reason || '',
              dosageVariations: s.dosageVariations.map((v) => ({
                ...v,
                dismissed: true,
                dismissReason: reason || '',
              })),
            };
          }
          // Per-dosage dismissal
          const updatedVariations = s.dosageVariations.map((v) =>
            v.dosage === dosageKey
              ? { ...v, dismissed: true, dismissReason: reason || '' }
              : v,
          );
          const allDismissed = updatedVariations.every((v) => v.dismissed);
          return {
            ...s,
            dosageVariations: updatedVariations,
            dismissed: allDismissed,
            dismissReason: allDismissed ? 'All dosages dismissed individually' : s.dismissReason,
          };
        }),
      });
    },
    [projectId, data],
  );

  const handleUndismiss = useCallback(
    async (canonicalName: string, dosageKey?: string) => {
      if (!projectId || !data) return;
      await api.doseUnit.undismiss(projectId, canonicalName, dosageKey);
      Telemetry.findingUndismissed({ feature: 'dose_unit', finding_id: dosageKey ? `${canonicalName}:${dosageKey}` : canonicalName });
      setData({
        ...data,
        substances: data.substances.map((s) => {
          if (s.canonicalName !== canonicalName) return s;
          if (dosageKey === undefined) {
            // Whole-substance undismiss — clears all
            return {
              ...s,
              dismissed: false,
              dismissReason: null,
              dosageVariations: s.dosageVariations.map((v) => ({
                ...v,
                dismissed: false,
                dismissReason: null,
              })),
            };
          }
          // Per-dosage undismiss
          const updatedVariations = s.dosageVariations.map((v) =>
            v.dosage === dosageKey
              ? { ...v, dismissed: false, dismissReason: null }
              : v,
          );
          return {
            ...s,
            dosageVariations: updatedVariations,
            dismissed: false,
            dismissReason: null,
          };
        }),
      });
    },
    [projectId, data],
  );

  // Filter + search
  const filtered = useMemo(() => {
    if (!data) return [];
    let items = data.substances;
    if (hideDismissed) {
      items = items.filter((s) => !s.dismissed);
    }
    if (statusFilter === 'consistent') {
      items = items.filter((s) => s.isConsistent);
    } else if (statusFilter === 'inconsistent') {
      items = items.filter((s) => !s.isConsistent);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      items = items.filter(
        (s) =>
          s.canonicalName.toLowerCase().includes(q) ||
          s.displayName.toLowerCase().includes(q) ||
          s.dosageVariations.some((v) => v.dosage.toLowerCase().includes(q)),
      );
    }
    return items;
  }, [data, statusFilter, searchText, hideDismissed]);

  // A2F-474: Classify findings into three tiers + sort
  const tieredItems = useMemo((): TieredEntry[] => {
    if (!isFiltering || !data) return filtered.map(f => ({ ...f, tier: 'new' as const }));

    const tierOrder: Record<FindingTier, number> = { new: 0, 'pre-existing': 1, inherited: 2 };
    const statusValue = (e: DoseUnitEntry) => (e.isConsistent ? 1 : 0);

    const items: TieredEntry[] = filtered.map(item => ({
      ...item,
      tier: classifyTier(item, data.documentMap),
    }));

    if (scopeFilter === 'in-scope' || scopeFilter === 'this-sub') {
      items.sort((a, b) =>
        tierOrder[a.tier] - tierOrder[b.tier]
        || statusValue(a) - statusValue(b)
      );
    } else {
      items.sort((a, b) => statusValue(a) - statusValue(b));
    }
    return items;
  }, [filtered, isFiltering, data, classifyTier, scopeFilter]);

  const tierCounts = useMemo(() => {
    if (!isFiltering) return { new: 0, preExisting: 0, inherited: 0 };
    return {
      new: tieredItems.filter(i => i.tier === 'new').length,
      preExisting: tieredItems.filter(i => i.tier === 'pre-existing').length,
      inherited: tieredItems.filter(i => i.tier === 'inherited').length,
    };
  }, [tieredItems, isFiltering]);

  const displayItems = useMemo(() => {
    if (!isFiltering) return tieredItems;
    return applyScopeFilter(tieredItems, scopeFilter);
  }, [tieredItems, isFiltering, scopeFilter]);

  // A2F-487: per-card 3-bucket breakdown (see AbbreviationsPage for rationale)
  const cardCounts = useMemo(() => {
    if (!isFiltering) return null;
    return {
      total: countByScope(tieredItems),
      consistent: countByScope(tieredItems.filter(s => s.isConsistent)),
      inconsistent: countByScope(tieredItems.filter(s => !s.isConsistent && !s.dismissed)),
    };
  }, [isFiltering, tieredItems]);

  // A2F-488: tile-click filter shortcuts (see AbbreviationsPage)
  const handleTileFilter = useCallback((status: string, scope: ScopeFilter) => {
    setStatusFilter(status as StatusFilter);
    setScopeFilter(scope);
    Telemetry.reviewTileFilterClicked({ page: 'dose-unit', status, scope });
  }, []);

  const handleStatusOnlyFilter = useCallback((status: string) => {
    setStatusFilter(status as StatusFilter);
    Telemetry.reviewTileFilterClicked({ page: 'dose-unit', status, scope: 'all' });
  }, []);

  const totalPages = Math.max(1, Math.ceil(displayItems.length / PAGE_SIZE));
  const pageItems = displayItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [statusFilter, searchText, hideDismissed, scopeFilter]);

  if (!activeProject) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-400">
            Select a project to scan dose/unit consistency.
          </p>
        </div>
      </div>
    );
  }

  // --- Loading ---
  if (uiState === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <LoaderCircle className="w-6 h-6 text-brand-orange animate-spin" />
        <p className="text-sm text-slate-500">Checking for cached results...</p>
      </div>
    );
  }

  // --- Scanning ---
  if (uiState === 'scanning') {
    return <ScanProgress state={taskState} onCancel={cancelScan} featureLabel="substance-dosage pairs" />;
  }

  // --- Landing ---
  if (uiState === 'landing') {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center space-y-4">
          <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
            Chrona Review
          </p>
          <h1 className="text-xl font-bold text-brand-charcoal">
            Dose/Unit
          </h1>
          <p className="text-sm text-slate-500">
            Scan your documents for substance-dosage consistency — detect
            when the same substance appears with different dosages across
            documents (e.g., &ldquo;acetaminophen 500 mg&rdquo; vs
            &ldquo;acetaminophen 250 mg&rdquo;).
          </p>
          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          <button
            onClick={startScan}
            disabled={!canRunScans || scanBusy}
            title={!canRunScans ? 'Requires Contributor role' : scanBusy ? 'Scan in progress' : undefined}
            className={`inline-flex items-center gap-2 px-5 py-2.5 bg-brand-orange text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors ${!canRunScans || scanBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {scanBusy ? 'Scan in progress\u2026' : 'Start Scan'}
          </button>
        </div>
      </div>
    );
  }

  // --- Results ---
  if (!data) return null;
  const { summary } = data;

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 bg-slate-800 text-white text-sm rounded-lg shadow-lg animate-fade-in">
          {toast}
        </div>
      )}

      {/* Header */}
      <div>
        <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
          Chrona Review
        </p>
        <h1 className="text-xl font-bold text-brand-charcoal">
          Dose/Unit
        </h1>
      </div>


      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Summary cards — A2F-487/488: click-to-filter tiles.
          Files Scanned stays non-clickable (scan-stats metric). */}
      {isFiltering && cardCounts ? (
        <div className="grid grid-cols-4 gap-4">
          <ScopeBreakdownCard label="Total Substances" color="slate" {...cardCounts.total}
            statusValue="all" activeStatus={statusFilter} activeScope={scopeFilter} onFilter={handleTileFilter} />
          <ScopeBreakdownCard label="Consistent" color="green" {...cardCounts.consistent}
            statusValue="consistent" activeStatus={statusFilter} activeScope={scopeFilter} onFilter={handleTileFilter} />
          <ScopeBreakdownCard label="Inconsistent" color="orange" {...cardCounts.inconsistent}
            statusValue="inconsistent" activeStatus={statusFilter} activeScope={scopeFilter} onFilter={handleTileFilter} />
          <SummaryStatCard label="Files Scanned" value={summary.scannedFiles} color="slate" />
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-4">
          <SummaryStatCard label="Total Substances" value={summary.totalSubstances} color="slate"
            statusValue="all" activeStatus={statusFilter} onFilter={handleStatusOnlyFilter} />
          <SummaryStatCard label="Consistent" value={summary.consistent} color="green"
            statusValue="consistent" activeStatus={statusFilter} onFilter={handleStatusOnlyFilter} />
          <SummaryStatCard label="Inconsistent" value={summary.inconsistent} color="orange"
            statusValue="inconsistent" activeStatus={statusFilter} onFilter={handleStatusOnlyFilter} />
          <SummaryStatCard label="Files Scanned" value={summary.scannedFiles} color="slate" />
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search substances or dosages..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange bg-white"
        >
          <option value="all">All Statuses</option>
          <option value="consistent">Consistent</option>
          <option value="inconsistent">Inconsistent</option>
        </select>
        {dismissedCount > 0 && (
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hideDismissed}
              onChange={(e) => setHideDismissed(e.target.checked)}
              className="rounded border-slate-300 text-brand-orange focus:ring-brand-orange/30"
            />
            <EyeOff className="w-3.5 h-3.5 text-slate-400" />
            Hide dismissed ({dismissedCount})
          </label>
        )}
        {isFiltering && (
          <ScopeFilterSelect
            value={scopeFilter}
            onChange={setScopeFilter}
            thisSubCount={tierCounts.new}
            preExistingCount={tierCounts.preExisting}
            inheritedCount={tierCounts.inherited}
          />
        )}
        <div className="ml-auto">
          <button
            onClick={rescan}
            disabled={!canRunScans || scanBusy}
            title={!canRunScans ? 'Requires Contributor role' : scanBusy ? 'Scan in progress' : undefined}
            className={`px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors ${!canRunScans || scanBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {scanBusy ? 'Scan in progress\u2026' : 'Rescan'}
          </button>
        </div>
      </div>

      {/* A2F-832: scope-filter-independent integrity notice (BRIEF gotcha #9) */}
      {isFiltering && <DataIntegrityNotice warnings={integrityWarnings} />}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-4 py-3 font-medium text-slate-600 w-8" />
              <th className="text-left px-4 py-3 font-medium text-slate-600 w-8">
                Status
              </th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">
                Substance
              </th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">
                Dosages
              </th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">
                Documents
              </th>
              {isFiltering && (
                <th className="text-left px-4 py-3 font-medium text-slate-600">
                  Scope
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 && (
              <tr>
                <td colSpan={isFiltering ? 6 : 5} className="px-4 py-8 text-center text-slate-400">
                  No substances match your filters.
                </td>
              </tr>
            )}
            {pageItems.map((entry) => (
              <SubstanceRow
                key={entry.canonicalName}
                entry={entry}
                tier={isFiltering ? (entry as TieredEntry).tier : null}
                tierConfident={entry.tierConfident}
                expanded={expandedRows.has(entry.canonicalName)}
                onToggle={() => toggleRow(entry.canonicalName)}
                documentMap={data.documentMap}
                onDismiss={handleDismiss}
                onUndismiss={handleUndismiss}
                onOpenDoc={handleOpenDoc}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            Showing {page * PAGE_SIZE + 1}&ndash;
            {Math.min((page + 1) * PAGE_SIZE, displayItems.length)} of{' '}
            {displayItems.length}
          </span>
          <div className="flex gap-1">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <ScanFooter data={data} />
    </div>
  );
}

// --- Sub-components ---


function SubstanceRow({
  entry,
  tier,
  tierConfident,
  expanded,
  onToggle,
  documentMap,
  onDismiss,
  onUndismiss,
  onOpenDoc,
}: {
  entry: DoseUnitEntry;
  tier: FindingTier | null;
  tierConfident?: boolean;
  expanded: boolean;
  onToggle: () => void;
  documentMap: Record<string, string>;
  onDismiss: (name: string, reason: string, dosageKey?: string) => Promise<void>;
  onUndismiss: (name: string, dosageKey?: string) => Promise<void>;
  onOpenDoc: (docName: string, substanceName: string) => void;
}) {
  const { canDismissFindings } = usePermissions();
  const [dismissing, setDismissing] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyDosage, setBusyDosage] = useState<string | null>(null);
  const [dismissingDosage, setDismissingDosage] = useState<string | null>(null);
  const [dosageReason, setDosageReason] = useState('');

  const rowOpacity = entry.dismissed ? 'opacity-50' : '';

  const confirmDismiss = async () => {
    setBusy(true);
    try {
      await onDismiss(entry.canonicalName, dismissReason);
      setDismissing(false);
      setDismissReason('');
    } finally {
      setBusy(false);
    }
  };

  const confirmUndismiss = async () => {
    setBusy(true);
    try {
      await onUndismiss(entry.canonicalName);
    } finally {
      setBusy(false);
    }
  };

  const confirmDismissDosage = async (dosage: string) => {
    setBusyDosage(dosage);
    try {
      await onDismiss(entry.canonicalName, dosageReason, dosage);
      setDismissingDosage(null);
      setDosageReason('');
    } finally {
      setBusyDosage(null);
    }
  };

  const undismissDosage = async (dosage: string) => {
    setBusyDosage(dosage);
    try {
      await onUndismiss(entry.canonicalName, dosage);
    } finally {
      setBusyDosage(null);
    }
  };

  return (
    <>
      <tr
        className={`border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer ${rowOpacity}`}
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={entry.isConsistent ? 'consistent' : 'inconsistent'} feature="dose_unit" />
        </td>
        <td className="px-4 py-3 font-semibold text-brand-charcoal">
          {entry.displayName}
        </td>
        <td className="px-4 py-3 text-slate-500">
          {entry.dosageVariations.length} dosage{entry.dosageVariations.length !== 1 ? 's' : ''}
        </td>
        <td className="px-4 py-3 text-slate-500">{entry.documentsCount}</td>
        {tier && (
          <td className="px-4 py-3">
            <span className="inline-flex items-center gap-0.5">
              <ScopePill tier={tier} />
              {/* A2F-832: per-finding indicator when tier was conservatively assigned */}
              {tierConfident === false && <TierUncertaintyIndicator />}
            </span>
          </td>
        )}
      </tr>
      {expanded && (
        <tr className={`bg-slate-50/30 ${rowOpacity}`}>
          <td colSpan={tier ? 6 : 5} className="px-8 py-4 space-y-3">
            {/* Dosage variations — grouped by unit type */}
            <div className="space-y-4">
              {groupByUnit(entry.dosageVariations).map(({ category, items }) => (
              <div key={category}>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {category}
              </p>
              <div className="flex flex-wrap gap-2">
                {items.map((v) => {
                  const isDosageDismissed = v.dismissed === true;
                  return (
                    <div
                      key={v.dosage}
                      className={`inline-flex flex-col items-start px-3 py-2 rounded-lg text-xs border ${
                        isDosageDismissed
                          ? 'bg-slate-50 border-slate-200 opacity-50'
                          : entry.dosageVariations.length === 1
                            ? 'bg-green-50 border-green-200'
                            : 'bg-orange-50 border-orange-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <span className="font-mono font-semibold text-sm">
                          {v.dosage}
                        </span>
                        {!entry.dismissed && entry.dosageVariations.length > 1 && (
                          isDosageDismissed ? (
                            <button
                              className={`ml-auto text-brand-orange hover:text-orange-600 text-[10px] font-medium ${!canDismissFindings ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={canDismissFindings ? 'Restore this dosage' : 'Requires Contributor role'}
                              disabled={busyDosage === v.dosage || !canDismissFindings}
                              onClick={(e) => {
                                e.stopPropagation();
                                undismissDosage(v.dosage);
                              }}
                            >
                              {busyDosage === v.dosage ? '...' : 'Restore'}
                            </button>
                          ) : (
                            <button
                              className={`ml-auto text-slate-400 hover:text-slate-600 text-[10px] font-medium ${!canDismissFindings ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={canDismissFindings ? 'Dismiss this dosage' : 'Requires Contributor role'}
                              disabled={busyDosage === v.dosage || !canDismissFindings}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDismissingDosage(v.dosage);
                                setDosageReason('');
                              }}
                            >
                              Dismiss
                            </button>
                          )
                        )}
                      </div>
                      <span className="text-slate-500 mt-0.5">
                        {v.count} mention{v.count !== 1 ? 's' : ''} in{' '}
                        {v.documents.length} doc{v.documents.length !== 1 ? 's' : ''}
                      </span>
                      {isDosageDismissed && v.dismissReason && (
                        <span className="text-slate-400 mt-0.5 italic">
                          {v.dismissReason}
                        </span>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {v.documents.map((doc) => (
                          <button
                            key={doc}
                            className="text-brand-orange hover:underline text-[11px]"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenDoc(doc, entry.displayName);
                            }}
                          >
                            {formatSectionLabel(doc)}
                          </button>
                        ))}
                      </div>
                      {dismissingDosage === v.dosage && (
                        <div className="mt-2 w-full space-y-1.5" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            placeholder="Reason (min 10 chars)"
                            value={dosageReason}
                            onChange={(e) => setDosageReason(e.target.value)}
                            className="w-full text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-orange"
                            autoFocus
                          />
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => confirmDismissDosage(v.dosage)}
                              disabled={busyDosage === v.dosage || dosageReason.trim().length < 10}
                              className="px-2 py-0.5 text-[10px] font-medium text-white bg-brand-orange rounded hover:bg-orange-600 disabled:opacity-50"
                            >
                              {busyDosage === v.dosage ? '...' : 'Confirm'}
                            </button>
                            <button
                              onClick={() => { setDismissingDosage(null); setDosageReason(''); }}
                              className="px-2 py-0.5 text-[10px] font-medium text-slate-500 border border-slate-200 rounded hover:bg-slate-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </div>
              ))}
            </div>

            {/* Dismiss / Restore actions (substance level) */}
            {entry.dismissed ? (
              <div className="pt-2 border-t border-slate-100 space-y-2">
                {entry.dismissReason && (
                  <p className="text-xs text-slate-500">
                    <span className="font-medium">Dismiss reason:</span>{' '}
                    {entry.dismissReason}
                  </p>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmUndismiss();
                  }}
                  disabled={busy || !canDismissFindings}
                  title={canDismissFindings ? undefined : 'Requires Contributor role'}
                  className={`px-3 py-1.5 text-xs font-medium text-brand-orange border border-brand-orange/30 rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-50 ${!canDismissFindings ? 'cursor-not-allowed' : ''}`}
                >
                  {busy ? 'Restoring...' : 'Restore All'}
                </button>
              </div>
            ) : dismissing ? (
              <div
                className="pt-2 border-t border-slate-100 space-y-2"
                onClick={(e) => e.stopPropagation()}
              >
                <textarea
                  placeholder="Reason for dismissal (required, minimum 10 characters)"
                  value={dismissReason}
                  onChange={(e) => setDismissReason(e.target.value)}
                  rows={2}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange resize-none"
                />
                <p className={`text-xs mt-1 ${dismissReason.trim().length >= 10 ? 'text-green-500' : 'text-slate-400'}`}>
                  {dismissReason.trim().length}/10 characters
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={confirmDismiss}
                    disabled={busy || dismissReason.trim().length < 10}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                  >
                    {busy ? 'Dismissing...' : 'Dismiss All Dosages'}
                  </button>
                  <button
                    onClick={() => {
                      setDismissing(false);
                      setDismissReason('');
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="pt-2 border-t border-slate-100">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDismissing(true);
                  }}
                  disabled={!canDismissFindings}
                  title={canDismissFindings ? undefined : 'Requires Contributor role'}
                  className={`px-3 py-1.5 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors ${!canDismissFindings ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Dismiss All
                </button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
